import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../api/context";
import type { UiLayerKey, UiPrefsView } from "../../api/onboarding-types";

type Api = ReturnType<typeof useApi>;
const reads = new WeakMap<Api, Map<string, Promise<UiPrefsView>>>();

/** One shared prefs read per api client + workspace, so five layers cost one request. */
function readPrefs(api: Api, workspaceId: string, fresh = false): Promise<UiPrefsView> {
  let byWorkspace = reads.get(api);
  if (!byWorkspace) reads.set(api, (byWorkspace = new Map()));
  let pending = byWorkspace.get(workspaceId);
  if (!pending || fresh) {
    pending = api.getUiPrefs(workspaceId);
    byWorkspace.set(workspaceId, pending);
    pending.catch(() => byWorkspace.delete(workspaceId));
  }
  return pending;
}

/**
 * UX-19: a clutter layer (banner, tour, popover) is shown once per user. Dismissal is stored
 * server-side. While loading, or if the read fails, the layer stays hidden — never flash clutter.
 */
export function useUiLayer(workspaceId: string, layer: UiLayerKey) {
  const api = useApi();
  const [state, setState] = useState<"loading" | "visible" | "dismissed" | "unavailable">(
    "loading",
  );

  useEffect(() => {
    let live = true;
    readPrefs(api, workspaceId).then(
      (prefs) => live && setState(prefs.dismissed[layer] ? "dismissed" : "visible"),
      // OWN-366 (50.2): an unreadable pref is not a dismissal; it must not count as done.
      () => live && setState("unavailable"),
    );
    return () => {
      live = false;
    };
  }, [api, workspaceId, layer]);

  const dismiss = useCallback(() => {
    setState("dismissed");
    void api.dismissUiLayer(workspaceId, layer).then(
      (prefs) => reads.get(api)?.set(workspaceId, Promise.resolve(prefs)),
      () => undefined, // best-effort: it may show once more next visit
    );
  }, [api, workspaceId, layer]);

  return {
    show: state === "visible",
    ready: state !== "loading",
    dismissed: state === "dismissed",
    dismiss,
  };
}
